/**
 * Gift With Purchase & Cart Recommendations Tracking
 * Google Analytics 4 Event Tracking
 */

class CartTracking {
  constructor() {
    this.initGWPTracking();
    this.initRecommendationTracking();
  }

  // Track Gift With Purchase selections
  initGWPTracking() {
    document.addEventListener('click', (e) => {
      const gwpButton = e.target.closest('[data-add-gift-gwp]');
      if (!gwpButton) return;

      const giftOption = gwpButton.closest('.gift-option');
      if (!giftOption) return;

      const productId = giftOption.getAttribute('data-product-id');
      const variantId = giftOption.getAttribute('data-variant-id');
      const giftTitle = giftOption.querySelector('.gift-title')?.textContent || 'Unknown Gift';
      const currentTier = document.querySelector('[data-current-tier]')?.getAttribute('data-current-tier') || '0';
      const isDefault = giftOption.getAttribute('data-is-default') === 'true';

      // GA4 Event - Select Promotion
      this.sendGA4Event('select_promotion', {
        promotion_id: `gwp_tier_${currentTier}`,
        promotion_name: `Gift With Purchase - Tier ${currentTier}`,
        creative_name: giftTitle,
        creative_slot: isDefault ? 'default' : 'option_2',
        location_id: 'cart_drawer',
        items: [{
          item_id: productId,
          item_name: giftTitle,
          item_variant: variantId,
          price: 0,
          quantity: 1
        }]
      });

      // Custom event for detailed tracking
      this.sendGA4Event('gwp_gift_selected', {
        event_category: 'Gift With Purchase',
        event_label: giftTitle,
        product_id: productId,
        variant_id: variantId,
        gift_tier: currentTier,
        is_default_selection: isDefault,
        value: 0
      });
    });
  }

  // Track Product Recommendation clicks
  initRecommendationTracking() {
    document.addEventListener('click', (e) => {
      // Look for product links OR quick-add buttons
      const recLink = e.target.closest(
        '.product-recommendations a[href*="/products/"], ' +
        '.product-recommendations--mobile a[href*="/products/"], ' +
        '.recommendations-track a[href*="/products/"], ' +
        '.recommendations-track--mobile a[href*="/products/"], ' +
        '[id*="recommendations"] a[href*="/products/"]'
      );
      
      const quickAddButton = e.target.closest(
        '.product-recommendations button.quick-add__submit, ' +
        '.product-recommendations--mobile button.quick-add__submit, ' +
        '.recommendations-track button.quick-add__submit, ' +
        '.recommendations-track--mobile button.quick-add__submit, ' +
        '.product-recommendations [class*="quick-add"], ' +
        '.recommendations-track [class*="quick-add"]'
      );
      
      // If neither a link nor a quick-add button, exit
      if (!recLink && !quickAddButton) return;

      // Determine which element we're working with
      const clickedElement = recLink || quickAddButton;
      const clickType = recLink ? 'product_view' : 'quick_add';

      // Get the product card/slide element
      const slideElement = clickedElement.closest('.recommendation-slide') || 
                          clickedElement.closest('.recommendation-item') ||
                          clickedElement.closest('li') ||
                          clickedElement.closest('.card-wrapper') ||
                          clickedElement.closest('.card') ||
                          clickedElement.closest('[class*="product"]');
      
      const productCard = clickedElement.closest('[data-product-id]') || slideElement;
      
      // Try multiple ways to get the product title and clean it up
      const rawTitle = slideElement?.querySelector('.card__heading')?.textContent ||
                       slideElement?.querySelector('.product-title')?.textContent || 
                       slideElement?.querySelector('[class*="title"]')?.textContent ||
                       slideElement?.querySelector('h3')?.textContent ||
                       slideElement?.querySelector('h4')?.textContent ||
                       slideElement?.querySelector('a')?.getAttribute('title') || 
                       slideElement?.querySelector('a')?.getAttribute('aria-label') ||
                       clickedElement.getAttribute('aria-label') ||
                       'Unknown Product';
      
      // Clean up title: remove extra whitespace, newlines, and duplicate text
      const productTitle = rawTitle
        .replace(/\n/g, ' ')       // Replace all newlines with spaces
        .replace(/\s+/g, ' ')      // Replace multiple spaces with single space
        .trim()                     // Remove leading/trailing whitespace
        .replace(/(.+?)\s+\1/g, '$1'); // Remove duplicate text (e.g., "Title Title" -> "Title")
      
      // Get product ID from data attribute or extract from URL or button ID
      let productId = productCard?.getAttribute('data-product-id') || '';
      
      if (!productId && recLink?.href) {
        const urlMatch = recLink.href.match(/\/products\/([^/?]+)/);
        if (urlMatch) productId = urlMatch[1];
      }
      
      // Try to extract from quick-add button ID (e.g., "quick-add-cart-drawer8270209679556-submit-2")
      if (!productId && quickAddButton) {
        const buttonId = quickAddButton.id || '';
        const idMatch = buttonId.match(/cart-drawer(\d+)/);
        if (idMatch) productId = idMatch[1];
      }
      
      // Try to find product URL from any link in the slide
      let productUrl = recLink?.getAttribute('href') || slideElement?.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
      
      // Extract product handle from URL if we have it
      let productHandle = '';
      if (productUrl) {
        const handleMatch = productUrl.match(/\/products\/([^/?]+)/);
        if (handleMatch) productHandle = handleMatch[1];
      }
      
      // Determine if mobile or desktop
      const isMobile = clickedElement.closest('.product-recommendations--mobile') !== null ||
                      clickedElement.closest('.recommendations-track--mobile') !== null;
      
      // Try to get position in slider
      const trackElement = clickedElement.closest('.recommendations-track') || 
                          clickedElement.closest('.recommendations-track--mobile') ||
                          clickedElement.closest('ul');
      const slideParent = clickedElement.closest('.recommendation-slide') || 
                         clickedElement.closest('li') ||
                         clickedElement.closest('.card-wrapper');
      let position = 0;
      if (trackElement && slideParent) {
        const slides = Array.from(trackElement.children);
        position = slides.indexOf(slideParent) + 1;
      }

      // GA4 Enhanced Ecommerce - Select Item
      this.sendGA4Event('select_item', {
        item_list_id: isMobile ? 'cart_recommendations_mobile' : 'cart_recommendations_desktop',
        item_list_name: isMobile ? 'Cart Recommendations (Mobile)' : 'Cart Recommendations (Desktop)',
        items: [{
          item_id: productId || productHandle,
          item_name: productTitle,
          index: position || undefined,
          item_list_id: 'cart_recommendations',
          item_list_name: 'Cart Drawer Recommendations'
        }]
      });

      // Custom event with more context
      this.sendGA4Event('cart_recommendation_click', {
        event_category: 'Cart Recommendations',
        event_label: productTitle,
        product_id: productId,
        product_handle: productHandle,
        product_url: productUrl,
        click_type: clickType,
        position: position || 0,
        device_type: isMobile ? 'mobile' : 'desktop'
      });
    });
  }

  // Helper to send GA4 events
  sendGA4Event(eventName, eventParams) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...eventParams
    });
  }

  // Helper retained for compatibility; UA tracking disabled
  sendUAEvent(category, action, label, value = null) {
    return;
  }
}

// Initialize tracking when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartTracking = new CartTracking();
  });
} else {
  window.cartTracking = new CartTracking();
}